import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * Confirmacion con modal propio, en reemplazo de window.confirm.
 *
 * El dialogo del navegador rompe la estetica del panel, muestra el dominio
 * ("ahasolutionsas.com dice") y no se puede dar estilo. Aqui se reemplaza por
 * un modal, pero conservando la forma de uso: la funcion devuelve una promesa
 * con true o false, asi que en cada pantalla solo cambia
 *
 *     if (!window.confirm('...')) return;
 * por
 *     if (!await confirmar('...')) return;
 *
 * Uso:
 *     const confirmar = useConfirm();
 *     if (!await confirmar({
 *       titulo:  'Eliminar renovacion',
 *       mensaje: '¿Eliminar la renovacion pendiente de "KVM 2"?',
 *       aceptar: 'Eliminar',
 *       peligro: true,
 *     })) return;
 *
 * Tambien acepta solo el texto: await confirmar('¿Continuar?')
 *
 * Con `campo` hace de window.prompt: devuelve el texto escrito, o null si se
 * cancela. Ojo al comprobarlo, porque '' tambien es cancelacion valida:
 *
 *     const motivo = await confirmar({
 *       titulo: 'Marcar como fallido',
 *       campo:  { etiqueta: 'Motivo del fallo', valor: 'Pago rechazado' },
 *     });
 *     if (motivo === null) return;
 */

const ConfirmContext = createContext(null);

const VACIO = {
  titulo: 'Confirmar',
  mensaje: '',
  detalle: '',
  aceptar: 'Aceptar',
  cancelar: 'Cancelar',
  peligro: false,
  // Un solo boton: sirve para dar un aviso que hay que leer, en lugar de un
  // mensaje que se desvanece solo y el usuario nunca llega a ver.
  soloAceptar: false,
  // { etiqueta, valor, tipo, marcador, requerido, ayuda } — convierte el
  // dialogo en un prompt: en vez de true devuelve lo que se haya escrito.
  campo: null,
};

export function ConfirmProvider({ children }) {
  const [opciones, setOpciones] = useState(null);
  // Guarda el resolve de la promesa mientras el modal esta abierto.
  const resolver = useRef(null);

  const confirmar = useCallback((entrada) => {
    const opts = typeof entrada === 'string' ? { mensaje: entrada } : (entrada || {});
    setOpciones({ ...VACIO, ...opts });
    return new Promise((resolve) => { resolver.current = resolve; });
  }, []);

  const responder = useCallback((valor) => {
    setOpciones(null);
    const r = resolver.current;
    resolver.current = null;
    if (r) r(valor);
  }, []);

  return (
    <ConfirmContext.Provider value={confirmar}>
      {children}
      {opciones && <DialogoConfirmacion {...opciones} onResponder={responder} />}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  const confirmar = useContext(ConfirmContext);
  if (!confirmar) {
    // Sin el proveedor no hay donde dibujar el modal. Se deja pasar la accion
    // en vez de bloquear la pantalla, y queda el aviso en consola.
    console.warn('useConfirm se uso fuera de ConfirmProvider: la accion se confirma sola.');
    return async () => true;
  }
  return confirmar;
}

function DialogoConfirmacion({ titulo, mensaje, detalle, aceptar, cancelar, peligro, soloAceptar, campo, onResponder }) {
  const botonAceptar = useRef(null);
  const entrada = useRef(null);
  const [texto, setTexto] = useState(campo ? String(campo.valor ?? '') : '');

  // Un campo obligatorio y vacio no deja aceptar: es el equivalente a que el
  // prompt del navegador devolviera '' y la pantalla siguiera adelante igual.
  const incompleto = !!campo && campo.requerido !== false && !String(texto).trim();

  // La respuesta de un dialogo con campo es el texto, no true; cancelar
  // siempre es null, para poder distinguirlo de un campo vacio.
  const responder = useCallback((ok) => {
    if (!campo) return onResponder(ok);
    onResponder(ok ? texto : null);
  }, [campo, texto, onResponder]);

  useEffect(() => {
    // En captura y cortando la propagacion: si esta confirmacion se abrio
    // encima de otro modal, Escape debe cerrar solo esta y no las dos.
    const alTeclear = (e) => {
      if (e.key === 'Escape' || e.key === 'Enter') {
        // Enter dentro de un textarea escribe una linea, no acepta.
        if (e.key === 'Enter' && e.target?.tagName === 'TEXTAREA') return;
        if (e.key === 'Enter' && incompleto) return;
        e.preventDefault();
        e.stopPropagation();
        responder(e.key === 'Enter');
      }
    };
    document.addEventListener('keydown', alTeclear, true);

    const desbordePrevio = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // Con campo, el foco va al campo y con el texto seleccionado: asi se
    // puede escribir encima del valor sugerido, igual que en el prompt.
    if (entrada.current) { entrada.current.focus(); entrada.current.select?.(); }
    else botonAceptar.current?.focus();

    return () => {
      document.removeEventListener('keydown', alTeclear, true);
      document.body.style.overflow = desbordePrevio;
    };
  }, [responder, incompleto]);

  return (
    // z-index por encima de .modal-overlay (1000): esta confirmacion casi
    // siempre nace desde un formulario que ya esta en un modal.
    <div className="modal-overlay" style={{ zIndex: 1100 }} onClick={() => responder(false)}>
      <div
        className="modal"
        style={{ maxWidth: 420 }}
        onClick={e => e.stopPropagation()}
        role="alertdialog"
        aria-modal="true"
        aria-label={titulo}
      >
        <div className="modal-header">
          <span className="modal-title">
            {peligro && <span style={{ marginRight: 8 }}>⚠️</span>}
            {titulo}
          </span>
        </div>

        <div className="modal-body">
          {mensaje && <p style={{ margin: 0, fontSize: '0.9rem', lineHeight: 1.5 }}>{mensaje}</p>}
          {detalle && (
            <p style={{ margin: 0, fontSize: '0.8rem', color: 'var(--color-text-muted)', lineHeight: 1.5 }}>
              {detalle}
            </p>
          )}

          {campo && (
            <div className="form-group" style={{ marginBottom: 0 }}>
              {campo.etiqueta && <label className="form-label">{campo.etiqueta}</label>}
              {campo.tipo === 'textarea' ? (
                <textarea
                  ref={entrada} className="form-input" rows={3}
                  placeholder={campo.marcador || ''}
                  value={texto} onChange={e => setTexto(e.target.value)}
                />
              ) : (
                <input
                  ref={entrada} className="form-input"
                  type={campo.tipo || 'text'}
                  min={campo.min} max={campo.max}
                  placeholder={campo.marcador || ''}
                  value={texto} onChange={e => setTexto(e.target.value)}
                />
              )}
              {campo.ayuda && (
                <div style={{ fontSize: '0.75rem', color: 'var(--color-text-muted)', marginTop: 4 }}>
                  {campo.ayuda}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          {!soloAceptar && (
            <button className="btn btn-ghost" onClick={() => responder(false)}>{cancelar}</button>
          )}
          <button
            ref={botonAceptar}
            className={`btn ${peligro ? 'btn-danger' : 'btn-primary'}`}
            onClick={() => responder(true)}
            disabled={incompleto}
          >
            {aceptar}
          </button>
        </div>
      </div>
    </div>
  );
}
